import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function FlipBook({ pages, id = "book", autoOpen = false, ratio = 1 }) {
  const leaves = pages.length / 2;
  const [flipped, setFlipped] = useState(0);
  const [active, setActive] = useState(null);
  const ref = useRef(null);
  const opened = useRef(false);
  const ptr = useRef({ x: 0, swiped: false });
  const timer = useRef(null);

  const go = (dir) => {
    setFlipped((f) => {
      const n = Math.min(leaves, Math.max(0, f + dir));
      if (n !== f) {
        setActive(dir > 0 ? f : n);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setActive(null), 1000);
      }
      return n;
    });
  };

  useEffect(() => {
    const el = ref.current;
    if (!el || !autoOpen) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !opened.current) {
        opened.current = true;
        setTimeout(() => go(1), 700);
        io.disconnect();
      }
    }, { threshold: 0.6 });
    io.observe(el);
    return () => { io.disconnect(); clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPointerDown = (e) => { ptr.current = { x: e.clientX, swiped: false }; };
  const onPointerUp = (e) => {
    const dx = e.clientX - ptr.current.x;
    if (Math.abs(dx) > 40) { ptr.current.swiped = true; go(dx < 0 ? 1 : -1); }
  };
  const faceClick = (dir) => () => { if (!ptr.current.swiped) go(dir); };
  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") go(1);
    if (e.key === "ArrowLeft") go(-1);
  };

  const shift = flipped === 0 ? -25 : flipped === leaves ? 25 : 0;
  const inner = pages.length - 2;
  const label = flipped === 0 ? "Front cover" : flipped === leaves ? "Back cover" : `Pages ${flipped * 2 - 1}–${Math.min(flipped * 2, inner)} of ${inner}`;
  const navBtn = "grid h-11 w-11 place-items-center rounded-full border border-[#FAF8F5]/20 text-[#FAF8F5] transition-colors hover:bg-[#FAF8F5]/10 disabled:opacity-30";

  return (
    <div className="w-full">
      <div
        ref={ref}
        data-testid={`${id}-book`}
        tabIndex={0}
        aria-label="Photo book. Use arrow keys to flip pages."
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        className="book mx-auto w-full max-w-[880px] touch-pan-y select-none outline-none"
        style={{ transform: `translateX(${shift}%)`, aspectRatio: `${2 * ratio} / 1` }}
      >
        <div className="book-edge" style={{ right: "100%", width: flipped * 2 }} />
        <div className="book-edge" style={{ left: "100%", width: (leaves - flipped) * 2 }} />
        {Array.from({ length: leaves }, (_, k) => {
          const isFlipped = k < flipped;
          return (
            <div
              key={k}
              data-testid={`${id}-leaf-${k}`}
              data-flipped={isFlipped}
              className={`leaf ${isFlipped ? "is-flipped" : ""}`}
              style={{ zIndex: active === k ? leaves + 10 : isFlipped ? k + 1 : leaves - k + 1 }}
            >
              <div className="leaf-face leaf-front" onClick={faceClick(1)}>{pages[2 * k]}</div>
              <div className="leaf-face leaf-back" onClick={faceClick(-1)}>{pages[2 * k + 1]}</div>
            </div>
          );
        })}
      </div>

      <div className="mt-10 flex items-center justify-center gap-4">
        <button type="button" data-testid={`${id}-prev-btn`} onClick={() => go(-1)} disabled={flipped === 0} aria-label="Previous page" className={navBtn}>
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span data-testid={`${id}-page-label`} className="min-w-[9rem] text-center font-mono text-xs uppercase tracking-[0.18em] text-[#FAF8F5]/60">{label}</span>
        <button type="button" data-testid={`${id}-next-btn`} onClick={() => go(1)} disabled={flipped === leaves} aria-label="Next page" className={navBtn}>
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
